/**
 * Utility functions for managing visitor IDs for analytics
 * Uses localStorage to persist visitor ID across sessions
 * GDPR-compliant: No PII is stored
 */

const VISITOR_ID_KEY = "smrt-menu-visitor-id";

/**
 * Generates a UUID v4
 */
function generateUUID(): string {
  // Use crypto.randomUUID() if available (modern browsers)
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Gets or creates a visitor ID
 * Returns existing ID from localStorage or generates a new one
 */
export function getVisitorId(): string {
  try {
    // Try to get existing visitor ID
    let visitorId = localStorage.getItem(VISITOR_ID_KEY);

    if (!visitorId) {
      // Generate new visitor ID
      visitorId = generateUUID();
      localStorage.setItem(VISITOR_ID_KEY, visitorId);
    }

    return visitorId;
  } catch {
    // If localStorage is not available (e.g., incognito mode),
    // generate a session-based ID
    console.warn("localStorage not available, using session-based visitor ID");
    return generateUUID();
  }
}

/**
 * Clears the visitor ID (for testing purposes)
 */
export function clearVisitorId(): void {
  try {
    localStorage.removeItem(VISITOR_ID_KEY);
  } catch {
    console.warn("Failed to clear visitor ID");
  }
}
